import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { AlertTriangle, TrendingUp, Users, Shield } from 'lucide-react';
import { toast } from 'sonner';

interface SuspiciousPattern {
  ip_address: string;
  signup_count: number;
  unique_emails: number;
  last_signup: string;
  emails: string[];
}

interface SignupStats {
  total_signups: number;
  successful_signups: number;
  failed_signups: number;
  success_rate: number;
}

export default function FraudMonitoring() {
  const [suspiciousPatterns, setSuspiciousPatterns] = useState<SuspiciousPattern[]>([]);
  const [stats, setStats] = useState<SignupStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFraudData();
  }, []);

  const loadFraudData = async () => {
    try {
      // Get suspicious patterns
      const { data: patterns, error: patternsError } = await supabase
        .from('suspicious_signup_patterns')
        .select('*');

      if (patternsError) throw patternsError;

      // Get signup stats for last 30 days
      const { data: attempts, error: attemptsError } = await supabase
        .from('signup_attempts')
        .select('success')
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      if (attemptsError) throw attemptsError;

      // Calculate stats
      const total = attempts?.length || 0;
      const successful = attempts?.filter(a => a.success).length || 0;
      const failed = total - successful;

      setSuspiciousPatterns(patterns || []);
      setStats({
        total_signups: total,
        successful_signups: successful,
        failed_signups: failed,
        success_rate: total > 0 ? (successful / total) * 100 : 0,
      });
    } catch (error) {
      console.error('Error loading fraud data:', error);
      toast.error('Failed to load fraud monitoring data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Fraud Monitoring</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Loading fraud detection data...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Total Signups
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_signups || 0}</div>
            <p className="text-xs text-muted-foreground">Last 30 days</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              Successful
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.successful_signups || 0}</div>
            <p className="text-xs text-muted-foreground">{stats?.success_rate.toFixed(1)}% success rate</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Failed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats?.failed_signups || 0}</div>
            <p className="text-xs text-muted-foreground">Authentication errors</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4 text-orange-500" />
              Suspicious
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{suspiciousPatterns.length}</div>
            <p className="text-xs text-muted-foreground">IPs with 4+ signups</p>
          </CardContent>
        </Card>
      </div>

      {/* Suspicious Patterns */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Suspicious Signup Patterns
          </CardTitle>
          <CardDescription>
            IP addresses with multiple trial signups (potential abuse)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {suspiciousPatterns.length === 0 ? (
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                No suspicious patterns detected. All signups appear normal.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              {suspiciousPatterns.map((pattern, index) => (
                <Alert key={index} variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">IP: {pattern.ip_address || 'Unknown'}</span>
                        <Badge variant="destructive">
                          {pattern.signup_count} signups
                        </Badge>
                      </div>
                      <div className="text-sm">
                        <p>Unique emails: {pattern.unique_emails}</p>
                        <p>Last signup: {new Date(pattern.last_signup).toLocaleString()}</p>
                        <p className="text-xs mt-2 opacity-75">
                          Emails: {pattern.emails.slice(0, 3).join(', ')}
                          {pattern.emails.length > 3 && ` +${pattern.emails.length - 3} more`}
                        </p>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle>Security Recommendations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-2">
            <Shield className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">Enable Email Verification</p>
              <p className="text-sm text-muted-foreground">
                Require users to verify their email before activating trials
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Shield className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">Review Suspicious IPs</p>
              <p className="text-sm text-muted-foreground">
                Manually review accounts from IPs with multiple signups
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Shield className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">Monitor Conversion Rates</p>
              <p className="text-sm text-muted-foreground">
                Track which trials convert to paid subscriptions
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}